import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../auth/authService';

declare global {
  interface Window {
    config?: {
      /** Minutes of inactivity before auto logout (default 15). Set in `public/config/default.js`. */
      idleLogoutMinutes?: number;
    };
  }
}

const DEFAULT_IDLE_MS = 30 * 60 * 1000;
/** High-frequency events are throttled to avoid timer resets every frame */
const ACTIVITY_THROTTLE_MS = 1000;

const WINDOW_ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'touchstart',
  'scroll',
  'click',
  'wheel',
] as const;

function getIdleDurationMs(): number {
  const raw = window.config?.idleLogoutMinutes;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
    return Math.floor(raw * 60 * 1000);
  }
  return DEFAULT_IDLE_MS;
}

/**
 * While the user has a valid RIS session (`authService`), resets an idle timer on activity.
 * After the configured idle duration with no activity, calls `authService.logout()` and redirects to `/login`.
 */
export function IdleSessionWatcher(): null {
  const navigate = useNavigate();
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastBumpRef = useRef(0);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current != null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const performIdleLogout = useCallback(async () => {
    if (!authService.isRisAuthenticated()) {
      return;
    }
    try {
      await authService.logout({ scope: 'ris' });
    } finally {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const armIdleTimer = useCallback(() => {
    clearIdleTimer();
    if (!authService.isRisAuthenticated()) {
      return;
    }
    const ms = getIdleDurationMs();
    idleTimerRef.current = setTimeout(() => {
      performIdleLogout();
    }, ms);
  }, [clearIdleTimer, performIdleLogout]);

  const bumpActivity = useCallback(
    (force: boolean) => {
      if (!authService.isRisAuthenticated()) {
        clearIdleTimer();
        return;
      }
      const now = Date.now();
      if (!force && now - lastBumpRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastBumpRef.current = now;
      armIdleTimer();
    },
    [armIdleTimer, clearIdleTimer]
  );

  useEffect(() => {
    const onAuthChanged = () => {
      if (authService.isRisAuthenticated()) {
        bumpActivity(true);
      } else {
        clearIdleTimer();
      }
    };

    onAuthChanged();
    window.addEventListener('userDataChanged', onAuthChanged);

    const listeners: Array<{ type: string; fn: EventListener }> = [];

    WINDOW_ACTIVITY_EVENTS.forEach(eventType => {
      const fn: EventListener = () => {
        bumpActivity(eventType !== 'mousemove');
      };
      window.addEventListener(eventType, fn, { passive: true });
      listeners.push({ type: eventType, fn });
    });

    return () => {
      window.removeEventListener('userDataChanged', onAuthChanged);
      listeners.forEach(({ type, fn }) => window.removeEventListener(type, fn));
      clearIdleTimer();
    };
  }, [bumpActivity, clearIdleTimer]);

  return null;
}
